import type {
  AssistedAffiliationDraft,
  AssistedAffiliationDraftPayload,
  AssistedAffiliationDraftProduct,
} from "@/services/assistedAffiliation/types";
import { STATUS_LABELS, STATUS_BY_LABEL, initialAssistedAffiliationForm } from "./constants";
import type { AssistedAffiliationForm, AssistedAffiliationStatus } from "./types";

// El servicio guarda el tramo de ventas como código y el formulario como la frase que se ve en
// pantalla. Un código que no esté aquí deja el campo vacío en vez de inventar un tramo.
const MONTHLY_SALES_BY_CODE: Record<string, string> = {
  "10K_500K": "RD$10,000 - RD$500,000",
  "500K_5M": "RD$500,000 - RD$5,000,000",
  "5M_15M": "RD$5,000,000 - RD$15,000,000",
  MAS_15M: "Más de RD$15,000,000",
};

const ACCOUNT_TYPE_BY_CODE: Record<string, string> = {
  AHORRO: "Ahorros",
  CORRIENTE: "Corriente",
};

const BILLET_SMART_PRODUCT_TYPE = "SMARTPOS";

// Solo se encienden los medios de cobro cuyo producto está confirmado: Billet Smart vive en
// `smartpos-device` y BilleTap es su propio tipo. Para los demás no hay correspondencia
// documentada, y encender un toggle por parecido mostraría un producto que nadie solicitó.
const CONFIRMED_PRODUCT_TOGGLES: Record<string, "billetSmart" | "billetap"> = {
  [BILLET_SMART_PRODUCT_TYPE]: "billetSmart",
  BILLET_TAP: "billetap",
};

const isBilletSmartProduct = (productType: string | undefined): boolean =>
  CONFIRMED_PRODUCT_TOGGLES[productType ?? ""] === "billetSmart";

// El servicio no borra productos al guardar: uno apagado se manda con este estado y sigue en el
// registro. Sin estado cuenta como activo, igual que en el servicio.
const INACTIVE_PRODUCT_STATUS = "INACTIVE";
const ACTIVE_PRODUCT_STATUS = "ACTIVE";

const isActiveProduct = (product: { status?: string | null } | null | undefined): boolean =>
  (product?.status ?? "").toUpperCase() !== INACTIVE_PRODUCT_STATUS;

// Billet Smart guarda en `product_props` códigos que en pantalla son etiquetas. Son los que entiende
// card-acceptance, donde termina la solicitud: su enum de conectividad y un booleano de propina.
const CONNECTIVITY_TYPE_BY_CODE: Record<string, string> = {
  SIM: "Data SIM",
  WIFI: "Wifi",
};

const TIPS_ENABLED_BY_LABEL: Record<string, boolean> = {
  Sí: true,
  No: false,
};

/**
 * Lee `product_props` como objeto. La columna es texto, así que puede llegar ya convertida o como el
 * JSON en texto: se aceptan las dos. Cualquier otra cosa cuenta como vacío, igual que un código
 * desconocido.
 */
const readProductProps = (value: unknown): Record<string, unknown> => {
  let parsed = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch (error) {
      return {};
    }
  }
  return parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)
    ? (parsed as Record<string, unknown>)
    : {};
};

type BilletSmartFields = Pick<
  AssistedAffiliationForm,
  "tapRate" | "tapQuantity" | "connectivityType" | "tipEnabled"
>;

const readBilletSmartFields = (product: AssistedAffiliationDraftProduct): BilletSmartFields => {
  const props = readProductProps(product.product_props);
  const rate = props.membership_rate;
  const connectivity = props.connectivity_type;

  return {
    tapRate: typeof rate === "number" && Number.isFinite(rate) ? String(rate) : "",
    tapQuantity:
      Number.isInteger(product.amount) && product.amount > 0 ? String(product.amount) : "",
    connectivityType:
      typeof connectivity === "string" ? CONNECTIVITY_TYPE_BY_CODE[connectivity] ?? "" : "",
    tipEnabled:
      Object.keys(TIPS_ENABLED_BY_LABEL).find(
        (label) => TIPS_ENABLED_BY_LABEL[label] === props.tips_enabled
      ) ?? "",
  };
};

/**
 * Traduce la solicitud que devuelve el servicio al modelo que edita el formulario.
 * Es una traducción CON PÉRDIDA: hay campos del formulario que el servicio no guarda, y esos
 * quedan en su valor inicial.
 */
export const draftToForm = (
  draft: AssistedAffiliationDraft
): AssistedAffiliationForm => {
  // Un producto apagado deja su medio de cobro apagado, pero sus datos se cargan igual: si el operador
  // lo vuelve a encender, la sección muestra lo que tenía.
  const productToggle = CONFIRMED_PRODUCT_TOGGLES[draft.product?.product_type];
  const activeToggle = isActiveProduct(draft.product) ? productToggle : undefined;

  return {
    ...initialAssistedAffiliationForm,
    documentNumber: draft.document_number ?? "",
    legalName: draft.legal_name ?? "",
    commercialName: draft.commercial_name ?? "",
    monthlySales: MONTHLY_SALES_BY_CODE[draft.average_sales] ?? "",
    // El select de tipo de negocio guarda el MCC; `detail_category` es solo su etiqueta.
    businessType: draft.commercial_category ?? "",
    representativeNames: draft.representative_first_name ?? "",
    representativeLastNames: draft.representative_last_name ?? "",
    representativeEmail: draft.representative_email ?? "",
    representativePhone: draft.representative_phone ?? "",
    isForeignRepresentative: Boolean(draft.representative_is_foreign),
    passportNumber: draft.representative_passport_number ?? "",
    nationality: draft.representative_nationality ?? "",
    // Los selects de geografía y banco guardan identificadores, no nombres.
    province: draft.province_id ?? "",
    municipality: draft.municipality_id ?? "",
    sector: draft.sector_id ?? "",
    neighborhood: draft.neighborhood_id ?? "",
    streetAndNumber: draft.street_and_number ?? "",
    reference: draft.reference ?? "",
    bank: draft.bank_id ?? "",
    accountNumber: draft.account_number ?? "",
    accountType: ACCOUNT_TYPE_BY_CODE[draft.bank_account] ?? "",
    internalComments: draft.description ?? "",
    // Se cargan de las columnas de la bandeja que llenan: si no, guardar las dejaría en blanco.
    executiveCode: draft.bhd_executive ?? "",
    commercialRepresentative: draft.commercial_assignee ?? "",
    // Un estado sin etiqueta se muestra con su código: el selector lo presenta como opción actual no
    // elegible, y al guardar se conserva el que tenía la solicitud.
    creationStatus:
      STATUS_LABELS[draft.status as AssistedAffiliationStatus] ?? draft.status ?? "",
    billetSmart: activeToggle === "billetSmart",
    terminalBillet: false,
    paymentLink: false,
    billetap: activeToggle === "billetap",
    // La sección de Billet Smart solo se llena desde una solicitud de Billet Smart.
    ...(productToggle === "billetSmart" ? readBilletSmartFields(draft.product) : {}),
  };
};

// Las tablas inversas se derivan de las de arriba en vez de escribirse a mano: dos listas paralelas
// se desincronizan en cuanto alguien agrega un tramo o un tipo de cuenta.
const invert = (table: Record<string, string>): Record<string, string> =>
  Object.fromEntries(
    Object.entries(table).map(([code, label]) => [label, code])
  );

const MONTHLY_SALES_BY_LABEL = invert(MONTHLY_SALES_BY_CODE);
const ACCOUNT_TYPE_BY_LABEL = invert(ACCOUNT_TYPE_BY_CODE);
const CONNECTIVITY_TYPE_BY_LABEL = invert(CONNECTIVITY_TYPE_BY_CODE);

const WHOLE_NUMBER = /^\d+$/;

// Las mismas reglas validan el formulario y arman lo que se guarda: si una cambia, cambian las dos.
export const TERMINAL_QUANTITIES = [1, 2, 3];
export const MAX_MEMBERSHIP_RATE = 4.75;
const RATE_FORMAT = /^\d+(\.\d{1,2})?$/;

/** Cantidad de terminales: 1, 2 o 3, o null si el texto no lo es. */
export const parseTerminalQuantity = (label: string): number | null => {
  const trimmed = label.trim();
  return WHOLE_NUMBER.test(trimmed) && TERMINAL_QUANTITIES.includes(Number(trimmed))
    ? Number(trimmed)
    : null;
};

/** Tasa de afiliación: entre 0.01 y 4.75 con hasta dos decimales, o null si el texto no lo es. */
export const parseMembershipRate = (label: string): number | null => {
  const trimmed = label.trim();
  if (!RATE_FORMAT.test(trimmed)) return null;
  const rate = Number(trimmed);
  return rate > 0 && rate <= MAX_MEMBERSHIP_RATE ? rate : null;
};

// Vacío es una decisión de quien edita y viaja como null. Un valor que no es una tasa válida no lo es:
// el guardado de borrador no bloquea por validaciones, así que se conserva la tasa que ya había.
const toRate = (label: string, previous: unknown): number | null => {
  if (!label.trim()) return null;
  return parseMembershipRate(label) ?? (typeof previous === "number" ? previous : null);
};

/**
 * Arma `product_props` de Billet Smart encima de lo que el registro ya traía, para no borrar llaves que
 * esta pantalla no conoce. Lo vacío viaja como null y nunca como "": la conectividad termina en un enum
 * de card-acceptance que no acepta "" y rechaza la solicitud entera. Si no queda nada que guardar
 * devuelve null, así abrir y guardar sin tocar la sección no convierte un registro sin datos en un
 * objeto de nulos.
 */
const toBilletSmartProps = (
  form: AssistedAffiliationForm,
  previous: Record<string, unknown>
): Record<string, unknown> | null => {
  const props = {
    ...previous,
    membership_rate: toRate(form.tapRate, previous.membership_rate),
    connectivity_type: CONNECTIVITY_TYPE_BY_LABEL[form.connectivityType] ?? null,
    tips_enabled: TIPS_ENABLED_BY_LABEL[form.tipEnabled] ?? null,
  };

  return Object.values(props).every((value) => value === null) ? null : props;
};

/**
 * Un producto Billet Smart armado desde el formulario, igual al que manda la creación. Sin una cantidad
 * válida lleva 1, el valor por defecto de la columna.
 */
const newBilletSmartProduct = (
  form: AssistedAffiliationForm
): NonNullable<AssistedAffiliationDraftPayload["product"]> => ({
  product_type: BILLET_SMART_PRODUCT_TYPE,
  amount: parseTerminalQuantity(form.tapQuantity) ?? 1,
  price: 0,
  status: "ACTIVE",
  product_props: toBilletSmartProps(form, {}),
});

/**
 * El producto que se guarda al editar. Billet Smart es el único cuya sección edita la pantalla; los
 * demás viajan como llegaron. Si la sección no se tocó, el producto tampoco: lo que la pantalla no sabe
 * mostrar (una tasa guardada como texto, un código desconocido) aparece vacío y se borraría al guardar.
 */
const toEditedProduct = (
  original: AssistedAffiliationDraft,
  form: AssistedAffiliationForm
): AssistedAffiliationDraft["product"] => {
  const product = original.product;
  if (!isBilletSmartProduct(product?.product_type)) {
    // Encender Billet Smart en una solicitud de otro producto, o sin producto, la vuelve de Billet
    // Smart con los datos de la sección, con la misma regla de la creación: el draft admite un solo
    // producto y Billet Smart es el primero confirmado. Si no, lo capturado en la sección se perdería.
    return form.billetSmart
      ? { ...newBilletSmartProduct(form), zoho_ticket_number: null, zoho_ticket_status: null }
      : product;
  }

  // Apagar Billet Smart no lo borra del registro: viaja con el estado inactivo y conserva sus datos.
  if (!form.billetSmart) {
    return isActiveProduct(product) ? { ...product, status: INACTIVE_PRODUCT_STATUS } : product;
  }

  const shown = readBilletSmartFields(product);
  const untouched = (Object.keys(shown) as (keyof BilletSmartFields)[]).every(
    (field) => shown[field] === form[field]
  );
  if (untouched && isActiveProduct(product)) return product;
  if (untouched) return { ...product, status: ACTIVE_PRODUCT_STATUS };

  return {
    ...product,
    status: ACTIVE_PRODUCT_STATUS,
    // La cantidad es un número sin valor vacío: sin una válida se conserva la del registro.
    amount: parseTerminalQuantity(form.tapQuantity) ?? product.amount,
    product_props: toBilletSmartProps(form, readProductProps(product.product_props)),
  };
};

/**
 * Traduce una etiqueta de pantalla al código que espera el servicio.
 * Un campo vacío significa que el usuario lo borró, y eso se envía. Solo cuando la etiqueta existe
 * pero no está en la tabla se conserva el valor previo, porque ahí el hueco es de la tabla y no una
 * decisión de quien edita.
 */
const toCode = (
  table: Record<string, string>,
  label: string,
  previous: string
): string => {
  if (!label) return "";
  return table[label] ?? previous;
};

/**
 * Arma el draft que se manda al guardar.
 * PARTE DE LA SOLICITUD ORIGINAL a propósito y solo sobreescribe lo que el formulario controla: el
 * servicio guarda más cosas de las que la pantalla muestra (los indicadores PEP, `mcc_itbis`, las
 * validaciones), y reconstruir el envío desde el formulario las borraría sin que se note.
 */
// "Representante legal" en la bandeja: nombres y apellidos del representante, en ese orden.
const toLegalRepresentative = (form: AssistedAffiliationForm) =>
  [form.representativeNames, form.representativeLastNames]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(" ");

// Las tres columnas de la bandeja que salen del formulario. Sin ellas, una solicitud creada o editada
// desde aquí aparecería en blanco en esas columnas.
const toTrayColumns = (form: AssistedAffiliationForm) => ({
  legal_representative: toLegalRepresentative(form),
  commercial_assignee: form.commercialRepresentative.trim(),
  bhd_executive: form.executiveCode.trim(),
});

// Nombre de un catálogo para el guardado: el que resolvió la pantalla; si no lo tiene y el id no
// cambió, el que ya traía la solicitud; si el id cambió, vacío antes que un nombre que ya no le
// corresponde.
const catalogName = (
  label: string | undefined,
  id: string,
  originalId: string,
  originalName: string | null | undefined
) => label || (id === originalId ? originalName ?? "" : "");

export const formToDraft = (
  original: AssistedAffiliationDraft,
  form: AssistedAffiliationForm,
  labels: Partial<DraftPayloadLabels> = {}
): AssistedAffiliationDraft => ({
  ...original,
  // Identifica la solicitud y no se edita. Se toma del original y no del formulario para que un
  // valor alterado en pantalla no pueda mandar el guardado a otro registro.
  document_number: original.document_number,
  legal_name: form.legalName,
  commercial_name: form.commercialName.trim(),
  description: form.internalComments.trim() || null,
  average_sales: toCode(MONTHLY_SALES_BY_LABEL, form.monthlySales, original.average_sales),
  commercial_category: form.businessType,
  detail_category: catalogName(
    labels.detailCategory,
    form.businessType,
    original.commercial_category,
    original.detail_category
  ),
  representative_first_name: form.representativeNames,
  representative_last_name: form.representativeLastNames,
  representative_email: form.representativeEmail,
  representative_phone: form.representativePhone,
  representative_is_foreign: form.isForeignRepresentative,
  representative_passport_number: form.passportNumber || null,
  representative_nationality: form.nationality,
  representative_country_code: form.nationality,
  ...toTrayColumns(form),
  province_id: form.province,
  province_name: catalogName(labels.provinceName, form.province, original.province_id, original.province_name),
  municipality_id: form.municipality,
  municipality_name: catalogName(
    labels.municipalityName,
    form.municipality,
    original.municipality_id,
    original.municipality_name
  ),
  sector_id: form.sector,
  sector_name: catalogName(labels.sectorName, form.sector, original.sector_id, original.sector_name),
  neighborhood_id: form.neighborhood,
  neighborhood_name: catalogName(
    labels.neighborhoodName,
    form.neighborhood,
    original.neighborhood_id,
    original.neighborhood_name
  ),
  street_and_number: form.streetAndNumber,
  reference: form.reference,
  bank_id: form.bank,
  account_number: form.accountNumber,
  bank_account: toCode(ACCOUNT_TYPE_BY_LABEL, form.accountType, original.bank_account),
  status: toCode(STATUS_BY_LABEL, form.creationStatus, original.status),
  product: toEditedProduct(original, form),
});

/**
 * Nombres que el servicio de creación pide además de los identificadores. El formulario guarda ids,
 * así que la pantalla los resuelve con sus catálogos y los pasa ya traducidos.
 */
export type DraftPayloadLabels = {
  provinceName: string;
  municipalityName: string;
  sectorName: string;
  neighborhoodName: string;
  detailCategory: string;
};

/**
 * Arma el payload de creación desde el formulario.
 * A diferencia del guardado, aquí no hay registro previo del cual partir: lo que el formulario no
 * captura viaja vacío en vez de inventado.
 */
export const formToDraftPayload = (
  form: AssistedAffiliationForm,
  labels: DraftPayloadLabels
): AssistedAffiliationDraftPayload => {
  return {
    document_number: form.documentNumber.trim(),
    document_type: "RNC",
    legal_name: form.legalName,
    commercial_name: form.commercialName.trim(),
    province_id: form.province,
    province_name: labels.provinceName,
    municipality_id: form.municipality,
    municipality_name: labels.municipalityName,
    sector_id: form.sector,
    sector_name: labels.sectorName,
    neighborhood_id: form.neighborhood,
    neighborhood_name: labels.neighborhoodName,
    street_and_number: form.streetAndNumber,
    reference: form.reference,
    // Sin comentarios viaja `null` y no un texto vacío, igual que en `formToDraft`.
    description: form.internalComments.trim() || null,
    commercial_category: form.businessType,
    detail_category: labels.detailCategory,
    // El formulario no distingue formal de informal, así que no se manda un valor inventado.
    business_type: "",
    average_sales: MONTHLY_SALES_BY_LABEL[form.monthlySales] ?? "",
    // Los documentos se resuelven cuando exista la subida con presigned URL: hasta entonces no hay
    // ninguna ruta real que mandar.
    rnc_certification: "",
    commercial_register: "",
    representative_act: "",
    bylaws: "",
    attachment: "",
    // El formulario no captura los indicadores PEP ni el de ITBIS.
    pep_company: false,
    pep_representative: false,
    rep_representative: false,
    mcc_itbis: false,
    account_number: form.accountNumber,
    bank_account: ACCOUNT_TYPE_BY_LABEL[form.accountType] ?? "",
    bank_id: form.bank,
    representative_phone: form.representativePhone,
    representative_email: form.representativeEmail,
    representative_first_name: form.representativeNames,
    representative_last_name: form.representativeLastNames,
    representative_is_foreign: form.isForeignRepresentative,
    representative_passport_number: form.passportNumber || null,
    representative_nationality: form.nationality,
    representative_country_code: form.nationality,
    ...toTrayColumns(form),
    // El estado inicial no depende del valor visible del formulario: una solicitud nueva siempre
    // debe persistirse como borrador. Los demás estados se gestionan después, durante la edición.
    status: "DRAFT",
    // El servicio solo acepta los tipos QR, MPOS, SMARTPOS y PAYMENTLINK, y de los medios de cobro del
    // formulario solo Billet Smart tiene correspondencia confirmada (SMARTPOS). Para los demás no se
    // manda producto: uno vacío o desconocido haría que el servicio rechazara la solicitud entera.
    ...(form.billetSmart ? { product: newBilletSmartProduct(form) } : {}),
  };
};
